import { useState } from "preact/hooks";
import { ArrowRightIcon } from "./components.tsx";
import { usePageMeta } from "./pageMeta.ts";
import { chatPath } from "./paths.ts";
import { PricingSection } from "./pricing.tsx";
import { businessSocialMeta } from "./socialMeta.ts";
import { agencyRoute } from "./agency.tsx";

const sectionClass = "py-16 px-4 sm:px-6 max-w-5xl mx-auto w-full";
const cardClass =
  "p-6 sm:p-8 rounded-2xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised transition-all duration-200 shadow-sm";

const MessageCircleIcon = (
  { className = "w-6 h-6" }: { className?: string },
) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
  </svg>
);

const UsersIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const CalendarIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const ShieldCheckIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <polyline points="9 12 11 14 15 10" />
  </svg>
);

const InteractiveBusinessDemo = () => {
  const [activeScenario, setActiveScenario] = useState<
    "support" | "leads" | "booking" | "operations"
  >("support");

  const scenarios = [
    {
      id: "support" as const,
      label: "24/7 Customer Support",
      customerMessage:
        "Hi! Do you have the ceramic dinner set in emerald green in stock, and can you deliver to Brooklyn by Friday?",
      botReply:
        "Yes! We have 4 emerald green sets left in our warehouse. If you order by 3 PM today, standard delivery arrives in Brooklyn by Thursday afternoon. Would you like me to reserve a set and send you a checkout link?",
      badge: "WhatsApp Business Support",
      detail:
        "Answers instant product, shipping, and policy questions from your catalog 24/7.",
    },
    {
      id: "leads" as const,
      label: "Inbound Lead Qualification",
      customerMessage:
        "Hello, we are looking for commercial HVAC maintenance for 3 office buildings in Austin. Can you share pricing?",
      botReply:
        "Thanks for reaching out! To give you an exact quote: what is the approximate square footage across the buildings, and are you looking for quarterly or bi-monthly inspections? I can also arrange a free site assessment with our Austin team.",
      badge: "Lead Capture & Qualification",
      detail:
        "Qualifies high-intent prospects, captures key requirements, and alerts your sales team.",
    },
    {
      id: "booking" as const,
      label: "Appointment Booking",
      customerMessage:
        "I need a consultation with Dr. Miller next Tuesday morning if possible.",
      botReply:
        "Dr. Miller has openings next Tuesday at 9:30 AM and 11:15 AM. Which time works best for you? Once confirmed, I'll send an invite to your calendar and a WhatsApp reminder 2 hours before.",
      badge: "Automated Scheduling",
      detail:
        "Syncs directly with Google Calendar to prevent double-bookings and reduce no-shows.",
    },
    {
      id: "operations" as const,
      label: "Team Operations & Routing",
      customerMessage:
        "Customer urgent ticket #481: 'Water heater leaking in unit 4B'.",
      botReply:
        "Emergency ticket flagged. Messaged on-call technician Marcus via WhatsApp with tenant contact details and dispatched an automated acknowledgment to the resident.",
      badge: "Internal Workflow Routing",
      detail:
        "Routes urgent tasks to staff members across WhatsApp groups and tracks resolution.",
    },
  ];

  const current = scenarios.find((s) => s.id === activeScenario) ??
    scenarios[0];

  return (
    <div className="w-full my-8 text-left max-w-4xl mx-auto">
      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6 justify-center">
        {scenarios.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => setActiveScenario(s.id)}
            className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all ${
              activeScenario === s.id
                ? "bg-accent text-white shadow-md"
                : "bg-surface-raised-light dark:bg-surface-raised text-text-secondary-light dark:text-text-secondary hover:text-text-primary-light dark:hover:text-text-primary border border-border-subtle-light dark:border-border-subtle"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Showcase Card */}
      <div className="rounded-2xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised p-6 sm:p-8 shadow-md flex flex-col justify-between">
        <div>
          <div className="flex items-center justify-between border-b border-border-subtle-light dark:border-border-subtle pb-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 flex items-center justify-center font-bold text-base">
                💼
              </div>
              <div>
                <div className="font-semibold text-sm sm:text-base">
                  Your Business AI Assistant
                </div>
                <div className="text-xs text-text-tertiary-light dark:text-text-tertiary">
                  WhatsApp & Web Live Agent
                </div>
              </div>
            </div>
            <div className="text-xs font-semibold px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
              {current.badge}
            </div>
          </div>

          {/* Conversation Bubble */}
          <div className="space-y-4 text-sm sm:text-base max-w-2xl mx-auto">
            <div className="flex justify-start">
              <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-surface-overlay-light dark:bg-surface-overlay text-text-primary-light dark:text-text-primary p-4 border border-border-subtle-light dark:border-border-subtle leading-relaxed shadow-xs">
                <div className="text-xs font-medium text-text-tertiary-light dark:text-text-tertiary mb-1">
                  Customer
                </div>
                {current.customerMessage}
              </div>
            </div>

            <div className="flex justify-end">
              <div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-accent text-white p-4 shadow-sm leading-relaxed">
                <div className="text-xs font-medium text-white/80 mb-1">
                  Your Business Bot
                </div>
                {current.botReply}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 pt-4 border-t border-border-subtle-light dark:border-border-subtle text-xs sm:text-sm text-text-secondary-light dark:text-text-secondary flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500" />
          <span>{current.detail}</span>
        </div>
      </div>
    </div>
  );
};

const FaqSection = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      q: "How does the AI Builder build my business bot?",
      a: "You simply click 'Build Your Business Bot' and chat with our AI Builder. Tell it your business name, what services or products you sell, and how you want it to handle customers. The builder writes the prompt, connects the required skills, and creates your ready-to-use bot in under 5 minutes.",
    },
    {
      q: "Can I connect my official WhatsApp Business phone number?",
      a: "Yes. You can connect your existing WhatsApp phone number or dedicated business line. Customers message your normal business number and get instant AI responses 24/7.",
    },
    {
      q: "How does the bot learn my business policies, pricing, and FAQs?",
      a: "You can upload your PDF manuals, price lists, or service agreements directly to the bot. It references your documents to give accurate, truthful answers without hallucinating.",
    },
    {
      q: "What happens when a customer has a complex request or wants a human?",
      a: "The bot supports seamless human handoff. If a customer asks to speak to an owner, or has an inquiry outside the bot's guidelines, it pauses and notifies your team via WhatsApp or email.",
    },
    {
      q: "Can multiple team members interact with the bot in a group chat?",
      a: "Yes. You can add the bot to your internal staff WhatsApp or Telegram group. It can track tasks, answer team questions, and summarize discussions.",
    },
    {
      q: "Do I need technical skills or a software developer?",
      a: "Zero coding or technical knowledge required. Everything is configured conversationally with the AI Builder.",
    },
  ];

  return (
    <section className="py-16 px-4 sm:px-6 max-w-4xl mx-auto w-full text-left">
      <div className="text-center mb-12">
        <div className="text-xs uppercase font-semibold tracking-wider text-accent-light dark:text-accent mb-2">
          Questions & Answers
        </div>
        <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-4">
          Frequently Asked Questions
        </h2>
        <p className="text-text-secondary-light dark:text-text-secondary text-base sm:text-lg">
          Answers about setup, WhatsApp connection, and business automation.
        </p>
      </div>

      <div className="space-y-3">
        {faqs.map((faq, idx) => {
          const isOpen = openIndex === idx;
          return (
            <div
              key={idx}
              className="rounded-2xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised overflow-hidden"
            >
              <button
                type="button"
                onClick={() => setOpenIndex(isOpen ? null : idx)}
                className="w-full p-5 sm:p-6 text-left flex items-center justify-between gap-4 font-semibold text-base sm:text-lg"
              >
                <span>{faq.q}</span>
                <span
                  className={`text-xl transition-transform duration-200 text-text-tertiary ${
                    isOpen ? "rotate-45" : ""
                  }`}
                >
                  +
                </span>
              </button>
              {isOpen && (
                <div className="px-5 pb-5 sm:px-6 sm:pb-6 text-sm sm:text-base text-text-secondary-light dark:text-text-secondary leading-relaxed border-t border-border-subtle-light/60 dark:border-border-subtle/60 pt-4">
                  {faq.a}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export const BusinessPage = () => {
  usePageMeta({
    title: businessSocialMeta.title,
    description: businessSocialMeta.description,
    image: businessSocialMeta.image,
    twitterCard: "summary_large_image",
  });

  return (
    <div className="flex flex-col items-center justify-center w-full min-h-screen text-center">
      {/* Hero Section */}
      <section className="pt-20 pb-12 px-4 sm:px-6 max-w-5xl mx-auto w-full">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-xs font-semibold mb-6">
          <MessageCircleIcon className="w-4 h-4" />
          <span>Automate Customer Support & Sales</span>
        </div>

        <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-text-primary-light dark:text-text-primary max-w-4xl mx-auto leading-[1.1] mb-6">
          Custom AI employees for your business, <br />
          <span className="text-accent">built in a 5-minute chat.</span>
        </h1>

        <p className="text-lg sm:text-xl text-text-secondary-light dark:text-text-secondary max-w-2xl mx-auto leading-relaxed mb-8">
          Don't waste thousands on agency developers. Talk to our AI Builder to
          deploy a 24/7 AI employee that answers customer inquiries on WhatsApp,
          qualifies inbound leads, and manages appointment bookings.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-4">
          <a
            href={chatPath}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-accent hover:bg-accent-hover text-white font-semibold text-base shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
          >
            <span>Build Your Business Bot in Chat</span>
            <ArrowRightIcon className="w-5 h-5" />
          </a>
          <a
            href="#features"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-surface-raised-light dark:bg-surface-raised hover:bg-surface-overlay-light dark:hover:bg-surface-overlay border border-border-subtle-light dark:border-border-subtle text-text-primary-light dark:text-text-primary font-semibold text-base transition-all"
          >
            See capabilities
          </a>
        </div>

        <p className="text-xs text-text-tertiary-light dark:text-text-tertiary">
          Zero coding required. Connect your WhatsApp or web in minutes.
        </p>

        {/* Live Interactive Demo */}
        <InteractiveBusinessDemo />
      </section>

      {/* Feature Grid */}
      <section id="features" className={sectionClass}>
        <div className="text-center mb-12">
          <div className="text-xs uppercase font-semibold tracking-wider text-accent-light dark:text-accent mb-2">
            Built for Real Business Operations
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-4">
            Everything your business needs to scale
          </h2>
          <p className="text-text-secondary-light dark:text-text-secondary text-base sm:text-lg max-w-xl mx-auto">
            From first customer contact to scheduled booking, automate your
            day-to-day operations.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 text-left">
          <div className={cardClass}>
            <div className="w-12 h-12 rounded-xl bg-accent/10 text-accent flex items-center justify-center mb-4">
              <MessageCircleIcon className="w-6 h-6" />
            </div>
            <h3 className="font-semibold text-lg mb-2">
              WhatsApp for Business
            </h3>
            <p className="text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed">
              Connect directly to your business WhatsApp number. Customers get
              instant answers where they already message.
            </p>
          </div>

          <div className={cardClass}>
            <div className="w-12 h-12 rounded-xl bg-accent/10 text-accent flex items-center justify-center mb-4">
              <UsersIcon className="w-6 h-6" />
            </div>
            <h3 className="font-semibold text-lg mb-2">Lead Qualification</h3>
            <p className="text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed">
              Asks qualification questions, captures contact details, and
              notifies you when high-value buyers arrive.
            </p>
          </div>

          <div className={cardClass}>
            <div className="w-12 h-12 rounded-xl bg-accent/10 text-accent flex items-center justify-center mb-4">
              <CalendarIcon className="w-6 h-6" />
            </div>
            <h3 className="font-semibold text-lg mb-2">Calendar Booking</h3>
            <p className="text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed">
              Syncs with Google Calendar. Books consultations, handles timezone
              differences, and sends reminders.
            </p>
          </div>

          <div className={cardClass}>
            <div className="w-12 h-12 rounded-xl bg-accent/10 text-accent flex items-center justify-center mb-4">
              <ShieldCheckIcon className="w-6 h-6" />
            </div>
            <h3 className="font-semibold text-lg mb-2">Safe Human Handoff</h3>
            <p className="text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed">
              If a customer wants a human or has a complex issue, the bot
              transfers the chat seamlessly to your staff.
            </p>
          </div>
        </div>
      </section>

      {/* Pre-built Agency Specialists Callout */}
      <section className="py-4 px-4 sm:px-6 max-w-5xl mx-auto w-full text-center">
        <div className="p-6 sm:p-8 rounded-2xl border border-border-subtle-light dark:border-border-subtle bg-surface-overlay-light/60 dark:bg-surface-overlay/60 flex flex-col sm:flex-row items-center justify-between gap-4 text-left shadow-xs">
          <div>
            <h3 className="text-lg sm:text-xl font-bold text-text-primary-light dark:text-text-primary mb-1">
              Prefer ready-to-deploy specialist agents?
            </h3>
            <p className="text-sm text-text-secondary-light dark:text-text-secondary">
              Browse our Agency roster of 250+ pre-built roles for marketing,
              sales, design, and operations.
            </p>
          </div>
          <a
            href={agencyRoute}
            className="shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-surface-raised-light dark:bg-surface-raised hover:bg-surface-overlay-light dark:hover:bg-surface-overlay border border-border-subtle-light dark:border-border-subtle text-text-primary-light dark:text-text-primary font-semibold text-sm transition-all shadow-xs"
          >
            <span>Explore Agency Templates</span>
            <ArrowRightIcon className="w-4 h-4 text-accent" />
          </a>
        </div>
      </section>

      {/* Pricing Section */}
      <section className={sectionClass}>
        <PricingSection />
      </section>

      {/* FAQ Section */}
      <FaqSection />

      {/* Bottom CTA Banner */}
      <section className="py-20 px-4 sm:px-6 max-w-4xl mx-auto w-full text-center">
        <div className="p-10 sm:p-14 rounded-3xl bg-gradient-to-b from-accent-light/10 via-surface-raised-light to-surface-raised-light dark:from-accent/15 dark:via-surface-raised dark:to-surface-raised border border-accent/20 shadow-xl space-y-6">
          <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight max-w-2xl mx-auto leading-tight">
            Build your business bot in the next 5 minutes.
          </h2>
          <p className="text-base sm:text-lg text-text-secondary-light dark:text-text-secondary max-w-xl mx-auto leading-relaxed">
            Just chat with our AI Builder. Describe what you do and what you
            want the bot to handle. It builds and launches your custom assistant
            instantly.
          </p>
          <div className="pt-2 flex items-center justify-center">
            <a
              href={chatPath}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-9 py-4 rounded-xl bg-accent hover:bg-accent-hover text-white font-semibold text-base shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
            >
              <span>Start building with the AI Builder</span>
              <ArrowRightIcon className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
